/**
 * What is the player looking at — or what did they just tap — and is it close enough to
 * touch?
 *
 * One ray per frame from the camera through the crosshair, against the physics world.
 * The per-frame answer lives in a ref; the store hears about it only when the target
 * *changes* — a few times a second at the very most — which is the one deliberate,
 * change-triggered crossing from the frame loop into React state (see design.md).
 *
 * `useTapTarget` casts the same ray through an arbitrary screen point instead, on the
 * rare frame a tap arrives. Both share one filter: what the crosshair can act on and what
 * a thumb can act on must never disagree.
 */

import { useFrame, useThree } from '@react-three/fiber';
import { useRapier } from '@react-three/rapier';
import { useCallback, useRef } from 'react';
import { Vector3 } from 'three';
import type { FocusTarget, InteractiveKind } from '../game/interaction';
import type { ObjectKind } from '../game/profile/types';
import { isPlayable } from '../game/phase';
import type { Vec2 } from '../input/inputState';
import { useGameStore } from '../store/gameStore';

/** How far the player can reach, in metres from the eye. */
export const INTERACTION_REACH = 2.5;

/**
 * What engine components hang on rigid bodies so a ray hit can be interpreted.
 *
 * `objectKind` is written by the carryables and by nothing else (design D5): it is what
 * lets the pure decider ask whether the thing in the hands is a part of the thing under
 * the crosshair, without the store ever learning the level's contents.
 */
export type BodyUserData = {
  kind: InteractiveKind | 'player';
  id?: string;
  objectKind?: ObjectKind;
};

/**
 * Whether a body the ray met can be targeted at all. The player cannot target their own
 * capsule, nor the box in their hands — it sits dead centre and would otherwise eat every
 * ray. (Sensors — drop zones, doorway probes — are filtered by the caller, which has the
 * collider; this only needs the body behind it.)
 */
function isTargetableBody(userData: unknown, carriedObjectId: string | null): boolean {
  const data = userData as Partial<BodyUserData> | undefined;
  if (data?.kind === 'player') return false;
  return !(data?.kind === 'carryable' && data.id === carriedObjectId);
}

function targetFromUserData(data: unknown): FocusTarget | null {
  if (typeof data !== 'object' || data === null) return null;
  const { kind, id, objectKind } = data as Partial<BodyUserData>;
  if (typeof id !== 'string') return null;
  if (kind !== 'door' && kind !== 'carryable' && kind !== 'light-switch' && kind !== 'table') {
    return null;
  }
  // A door or a switch has no object kind, and the field stays off the target rather
  // than being carried as an explicit `undefined`.
  return objectKind ? { id, kind, objectKind } : { id, kind };
}

export function useInteractionTarget(): void {
  const { world, rapier } = useRapier();
  const camera = useThree((state) => state.camera);

  const rayRef = useRef<InstanceType<typeof rapier.Ray> | null>(null);
  const directionRef = useRef(new Vector3());
  const lastTargetRef = useRef<FocusTarget | null>(null);

  useFrame(() => {
    const store = useGameStore.getState();

    const publish = (target: FocusTarget | null) => {
      const last = lastTargetRef.current;
      if (last?.id === target?.id && last?.kind === target?.kind) return;
      lastTargetRef.current = target;
      store.setFocus(target);
    };

    if (!isPlayable(store.phase)) {
      publish(null);
      return;
    }

    const direction = camera.getWorldDirection(directionRef.current);
    // One Ray for the hook's lifetime, mutated in place — no per-frame allocation.
    rayRef.current ??= new rapier.Ray(camera.position, direction);
    const ray = rayRef.current;
    ray.origin = camera.position;
    ray.dir = direction;

    const hit = world.castRay(
      ray,
      INTERACTION_REACH,
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      (collider) =>
        !collider.isSensor() &&
        isTargetableBody(collider.parent()?.userData, store.carriedObjectId),
    );

    const body = hit?.collider.parent();
    publish(body ? targetFromUserData(body.userData) : null);
  });
}

/**
 * Resolve what sits under a point on the screen, given in normalized device coordinates.
 *
 * This is how a tap reaches an object that is not dead centre — the pad equivalent of
 * putting the crosshair on it first. Called at most once a frame, and only on the frames
 * a tap actually arrived; it still allocates nothing, reusing one `Ray` and one vector.
 */
export function useTapTarget(): (ndc: Vec2) => FocusTarget | null {
  const { world, rapier } = useRapier();
  const camera = useThree((state) => state.camera);

  const rayRef = useRef<InstanceType<typeof rapier.Ray> | null>(null);
  const directionRef = useRef(new Vector3());

  return useCallback(
    (ndc: Vec2) => {
      const { carriedObjectId } = useGameStore.getState();

      // Unproject a point on the near-to-far line through the tap, then aim at it from
      // the eye: the same ray the crosshair casts, rotated off centre.
      const direction = directionRef.current
        .set(ndc.x, ndc.y, 0.5)
        .unproject(camera)
        .sub(camera.position)
        .normalize();

      rayRef.current ??= new rapier.Ray(camera.position, direction);
      const ray = rayRef.current;
      ray.origin = camera.position;
      ray.dir = direction;

      const hit = world.castRay(
        ray,
        INTERACTION_REACH,
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        (collider) =>
          !collider.isSensor() && isTargetableBody(collider.parent()?.userData, carriedObjectId),
      );

      const body = hit?.collider.parent();
      return body ? targetFromUserData(body.userData) : null;
    },
    [world, rapier, camera],
  );
}
